import { useMemo, useState } from "react";
import { DeleteModal, PrimaryButton } from "../../components";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useParcels } from "../../store/useParcels";

dayjs.extend(relativeTime);

const MONTH_NUMBERS = Array.from({ length: 12 }, (_, index) => index + 1);

export const ParcelTable = () => {
  /** Hooks */
  const { allGroupedParcels } = useParcels();
  const navigate = useNavigate();
  /** State */
  const [isOpen, setIsOpen] = useState(false);

  const toggleModal = () => setIsOpen(!isOpen);

  const filteredParcels = useMemo(
    () =>
      allGroupedParcels?.filter((item) => item?.courier?.name !== "Budbee") ??
      [],
    [allGroupedParcels]
  );

  const monthlyTotals = useMemo(() => {
    const totals = MONTH_NUMBERS.reduce(
      (acc, month) => {
        let totalVolume = 0;
        let totalRevenue = 0;

        filteredParcels.forEach((courier) => {
          const monthData = courier?.months?.[month];
          if (monthData) {
            totalVolume += Number(monthData.totalVolume ?? 0);
            totalRevenue += Number(monthData.totalRevenue ?? 0);
          }
        });

        acc[month] = {
          totalVolume,
          totalRevenue,
        };

        return acc;
      },
      {} as Record<number, { totalVolume: number; totalRevenue: number }>
    );

    return totals;
  }, [filteredParcels]);

  return (
    <div className="relative overflow-x-auto">
      <table className="w-full text-base font-normal font-satoshi text-left rtl:text-right text-dark">
        <thead className="font-medium bg-gray-50">
          <tr>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
              Courier
            </th>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
              Jan
            </th>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
              Feb
            </th>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
              Mar
            </th>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
              Apr
            </th>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
              May
            </th>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
              Jun
            </th>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
              Jul
            </th>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
              Aug
            </th>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
              Sep
            </th>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
              Oct
            </th>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
              Nov
            </th>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
              Dec
            </th>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4 text-right">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredParcels?.map((item) => {
            return (
              <tr
                key={item.courier?._id}
                className="bg-white hover:bg-gray-100 cursor-pointer transition ease-linear border-b"
              >
                <td
                  onClick={() =>
                    navigate(`/view-courier-parcel/${item?.courier?._id}`)
                  }
                  className="px-2 sm:px-6 py-1 sm:py-4 break-all sm:whitespace-nowrap text-xs sm:text-base"
                >
                  {/* @ts-ignore */}
                  <p>{item?.courier?.name}</p>
                  <p className="text-sm">(Volume)</p>
                  <p className="text-sm">(Revenue)</p>
                </td>
                <td
                  onClick={() =>
                    navigate(`/view-courier-parcel/${item?.courier?._id}`)
                  }
                  className="px-2 sm:px-6 py-1 sm:py-4 text-xs sm:text-base"
                >
                  <p className="text-transparent">.</p>
                  <p className="text-right">
                    {item?.months?.[1]?.totalVolume ?? 0}
                  </p>
                  <p>
                    {new Intl.NumberFormat("nl-NL", {
                      style: "currency",
                      currency: "EUR",
                    }).format(
                      Number(item?.months?.[1]?.totalRevenue ?? 0) / 100
                    )}
                  </p>
                </td>
                <td
                  onClick={() =>
                    navigate(`/view-courier-parcel/${item?.courier?._id}`)
                  }
                  className="px-2 sm:px-6 py-1 sm:py-4 text-xs sm:text-base"
                >
                  <p className="text-transparent">.</p>
                  <p className="text-right">
                    {item?.months?.[2]?.totalVolume ?? 0}
                  </p>
                  <p>
                    {new Intl.NumberFormat("nl-NL", {
                      style: "currency",
                      currency: "EUR",
                    }).format(
                      Number(item?.months?.[2]?.totalRevenue ?? 0) / 100
                    )}
                  </p>
                </td>
                <td
                  onClick={() =>
                    navigate(`/view-courier-parcel/${item?.courier?._id}`)
                  }
                  className="px-2 sm:px-6 py-1 sm:py-4 text-xs sm:text-base"
                >
                  <p className="text-transparent">.</p>
                  <p className="text-right">
                    {item?.months?.[3]?.totalVolume ?? 0}
                  </p>
                  <p>
                    {new Intl.NumberFormat("nl-NL", {
                      style: "currency",
                      currency: "EUR",
                    }).format(
                      Number(item?.months?.[3]?.totalRevenue ?? 0) / 100
                    )}
                  </p>
                </td>
                <td
                  onClick={() =>
                    navigate(`/view-courier-parcel/${item?.courier?._id}`)
                  }
                  className="px-2 sm:px-6 py-1 sm:py-4 text-xs sm:text-base"
                >
                  <p className="text-transparent">.</p>
                  <p className="text-right">
                    {item?.months?.[4]?.totalVolume ?? 0}
                  </p>
                  <p>
                    {new Intl.NumberFormat("nl-NL", {
                      style: "currency",
                      currency: "EUR",
                    }).format(
                      Number(item?.months?.[4]?.totalRevenue ?? 0) / 100
                    )}
                  </p>
                </td>
                <td
                  onClick={() =>
                    navigate(`/view-courier-parcel/${item?.courier?._id}`)
                  }
                  className="px-2 sm:px-6 py-1 sm:py-4 text-xs sm:text-base"
                >
                  <p className="text-transparent">.</p>
                  <p className="text-right">
                    {item?.months?.[5]?.totalVolume ?? 0}
                  </p>
                  <p>
                    {new Intl.NumberFormat("nl-NL", {
                      style: "currency",
                      currency: "EUR",
                    }).format(
                      Number(item?.months?.[5]?.totalRevenue ?? 0) / 100
                    )}
                  </p>
                </td>
                <td
                  onClick={() =>
                    navigate(`/view-courier-parcel/${item?.courier?._id}`)
                  }
                  className="px-2 sm:px-6 py-1 sm:py-4 text-xs sm:text-base"
                >
                  <p className="text-transparent">.</p>
                  <p className="text-right">
                    {item?.months?.[6]?.totalVolume ?? 0}
                  </p>
                  <p>
                    {new Intl.NumberFormat("nl-NL", {
                      style: "currency",
                      currency: "EUR",
                    }).format(
                      Number(item?.months?.[6]?.totalRevenue ?? 0) / 100
                    )}
                  </p>
                </td>
                <td
                  onClick={() =>
                    navigate(`/view-courier-parcel/${item?.courier?._id}`)
                  }
                  className="px-2 sm:px-6 py-1 sm:py-4 text-xs sm:text-base"
                >
                  <p className="text-transparent">.</p>
                  <p className="text-right">
                    {item?.months?.[7]?.totalVolume ?? 0}
                  </p>
                  <p>
                    {new Intl.NumberFormat("nl-NL", {
                      style: "currency",
                      currency: "EUR",
                    }).format(
                      Number(item?.months?.[7]?.totalRevenue ?? 0) / 100
                    )}
                  </p>
                </td>
                <td
                  onClick={() =>
                    navigate(`/view-courier-parcel/${item?.courier?._id}`)
                  }
                  className="px-2 sm:px-6 py-1 sm:py-4 text-xs sm:text-base"
                >
                  <p className="text-transparent">.</p>
                  <p className="text-right">
                    {item?.months?.[8]?.totalVolume ?? 0}
                  </p>
                  <p>
                    {new Intl.NumberFormat("nl-NL", {
                      style: "currency",
                      currency: "EUR",
                    }).format(
                      Number(item?.months?.[8]?.totalRevenue ?? 0) / 100
                    )}
                  </p>
                </td>
                <td
                  onClick={() =>
                    navigate(`/view-courier-parcel/${item?.courier?._id}`)
                  }
                  className="px-2 sm:px-6 py-1 sm:py-4 text-xs sm:text-base"
                >
                  <p className="text-transparent">.</p>
                  <p className="text-right">
                    {item?.months?.[9]?.totalVolume ?? 0}
                  </p>
                  <p>
                    {new Intl.NumberFormat("nl-NL", {
                      style: "currency",
                      currency: "EUR",
                    }).format(
                      Number(item?.months?.[9]?.totalRevenue ?? 0) / 100
                    )}
                  </p>
                </td>
                <td
                  onClick={() =>
                    navigate(`/view-courier-parcel/${item?.courier?._id}`)
                  }
                  className="px-2 sm:px-6 py-1 sm:py-4 text-xs sm:text-base"
                >
                  <p className="text-transparent">.</p>
                  <p className="text-right">
                    {item?.months?.[10]?.totalVolume ?? 0}
                  </p>
                  <p>
                    {new Intl.NumberFormat("nl-NL", {
                      style: "currency",
                      currency: "EUR",
                    }).format(
                      Number(item?.months?.[10]?.totalRevenue ?? 0) / 100
                    )}
                  </p>
                </td>
                <td
                  onClick={() =>
                    navigate(`/view-courier-parcel/${item?.courier?._id}`)
                  }
                  className="px-2 sm:px-6 py-1 sm:py-4 text-xs sm:text-base"
                >
                  <p className="text-transparent">.</p>
                  <p className="text-right">
                    {item?.months?.[11]?.totalVolume ?? 0}
                  </p>
                  <p>
                    {new Intl.NumberFormat("nl-NL", {
                      style: "currency",
                      currency: "EUR",
                    }).format(
                      Number(item?.months?.[11]?.totalRevenue ?? 0) / 100
                    )}
                  </p>
                </td>
                <td
                  onClick={() =>
                    navigate(`/view-courier-parcel/${item?.courier?._id}`)
                  }
                  className="px-2 sm:px-6 py-1 sm:py-4 text-xs sm:text-base"
                >
                  <p className="text-transparent">.</p>
                  <p className="text-right">
                    {item?.months?.[12]?.totalVolume ?? 0}
                  </p>
                  <p>
                    {new Intl.NumberFormat("nl-NL", {
                      style: "currency",
                      currency: "EUR",
                    }).format(
                      Number(item?.months?.[12]?.totalRevenue ?? 0) / 100
                    )}
                  </p>
                </td>
                <td className="px-2 sm:px-6 py-1 sm:py-4 cursor-pointer">
                  <PrimaryButton
                    onClick={() =>
                      navigate(`/view-courier-parcel/${item?.courier?._id}`)
                    }
                    label="View"
                    textClassName="text-sm."
                  />
                </td>
              </tr>
            );
          })}
          {filteredParcels.length > 0 && (
            <tr className="bg-gray-50 font-semibold border-t">
              <td className="px-2 sm:px-6 py-1 sm:py-4 text-xs sm:text-base">
                Totals
              </td>
              {MONTH_NUMBERS.map((month) => (
                <td
                  key={`month-total-${month}`}
                  className="px-2 sm:px-6 py-1 sm:py-4 text-xs sm:text-base"
                >
                  <p className="text-transparent">.</p>
                  <p className="text-right">
                    {monthlyTotals[month]?.totalVolume ?? 0}
                  </p>
                  <p>
                    {new Intl.NumberFormat("nl-NL", {
                      style: "currency",
                      currency: "EUR",
                    }).format(
                      Number(monthlyTotals[month]?.totalRevenue ?? 0) / 100
                    )}
                  </p>
                </td>
              ))}
              <td />
            </tr>
          )}
        </tbody>
      </table>
      <DeleteModal isOpen={isOpen} closeModal={toggleModal} />
    </div>
  );
};
